import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import HeroIcon from "../common/HeroIcon";
import { formatEasyTime } from "../../utils/time";
import { PROMOTION_COPY } from "../brand/constants";

/**
 * Single idea card with cleaner layout: prominent title, description, why this works.
 * Uses darker card surface only; no nested backgrounds.
 */
const DEFAULT_PROMOTION = 5;

const IdeaCard = ({
  idea,
  selectedBrandHash,
  scriptPromptValue,
  scriptPromotionValue,
  isTweakOpen,
  onTweakOpenChange,
  onScriptPromptChange,
  onScriptPromotionChange,
  onToggleStar,
  onGenerateScript,
  isGeneratingScript,
  onSave,
  baseUrlSlash,
}) => {
  const scripts = Array.isArray(idea?.scripts) ? idea.scripts : [];
  const scriptsByDate = [...scripts].sort((a, b) => {
    const tA = a.create_date ? new Date(a.create_date).getTime() : 0;
    const tB = b.create_date ? new Date(b.create_date).getTime() : 0;
    return tB - tA;
  });
  const hasScripts = scriptsByDate.length > 0;

  return (
    <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 space-y-4">
      <div className="space-y-3">
        <h5 className="text-base font-semibold text-theme-text flex items-center gap-2 leading-tight">
          {idea.emoji ? <span className="text-lg">{idea.emoji}</span> : null}
          {idea.title || "Untitled idea"}
        </h5>
        <p className="text-sm text-theme-text opacity-90 leading-relaxed">
          {idea.content || "No idea details provided."}
        </p>
        {idea.why_this_works ? (
          <p className="text-sm text-theme-text opacity-75 italic border-l-2 border-theme-primary pl-3 py-1">
            Why this works: {idea.why_this_works}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1 justify-between">
        <div className="flex flex-col gap-1 text-xs text-theme-text opacity-60">
          {idea.create_date ? (
            <span className="inline-flex items-center gap-1.5">
              <HeroIcon name="clock" className="w-3.5 h-3.5 flex-shrink-0" />
              Generated {formatEasyTime(idea.create_date)}
            </span>
          ) : null}
          {idea.context?.promotion_score != null ? (
            <span
              className="inline-flex items-center gap-1.5"
              title={PROMOTION_COPY[idea.context.promotion_score] ?? ""}
            >
              <HeroIcon name="megaphone" className="w-3.5 h-3.5 flex-shrink-0" />
              Promotion {idea.context.promotion_score}/10
            </span>
          ) : null}
          {(idea.context?.user_prompt || "").trim() ? (
            <>
              <span
                className="inline-flex items-center gap-1.5 cursor-help underline decoration-dotted underline-offset-1"
                data-tooltip-id={`context-tooltip-${idea.hash}`}
                data-tooltip-content={idea.context.user_prompt}
              >
                <HeroIcon name="chat-bubble-left-ellipsis" className="w-3.5 h-3.5 flex-shrink-0" />
                Context added
              </span>
              <Tooltip id={`context-tooltip-${idea.hash}`} place="top" className="!max-w-[280px] !text-xs" />
            </>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onSave(idea.hash, !idea.starred)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-theme-secondary text-theme-text text-sm hover:bg-theme-main"
        >
          {idea.starred ? (
            <span className="text-theme-primary">★</span>
          ) : (
            <span className="text-theme-text opacity-50">☆</span>
          )}
          <span>{idea.starred ? "Saved" : "Save"}</span>
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => onTweakOpenChange(idea.hash === isTweakOpen ? null : idea.hash)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-theme-secondary text-theme-text text-sm hover:bg-theme-main"
          >
            <HeroIcon name="pencil-square" className="w-4 h-4" />
            Tweak Idea
          </button>
          {isTweakOpen ? (
            <>
              <div className="fixed inset-0 z-10" aria-hidden onClick={() => onTweakOpenChange(null)} />
              <div className="absolute left-0 top-full mt-2 w-72 p-3 rounded-lg border border-theme-secondary bg-theme-main shadow-lg z-20 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-theme-text mb-2">Promotion score</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={10}
                      step={1}
                      value={scriptPromotionValue ?? idea.context?.promotion_score ?? DEFAULT_PROMOTION}
                      onChange={(e) => {
                        const n = Math.max(0, Math.min(10, Math.round(Number(e.target.value))));
                        onScriptPromotionChange?.(idea.hash, n);
                      }}
                      className="w-full accent-theme-primary"
                    />
                    <input
                      type="number"
                      min={0}
                      max={10}
                      step={1}
                      value={scriptPromotionValue ?? idea.context?.promotion_score ?? DEFAULT_PROMOTION}
                      onChange={(e) => {
                        const v = e.target.value;
                        const n = v === "" ? null : Math.max(0, Math.min(10, Math.round(Number(v))));
                        onScriptPromotionChange?.(idea.hash, Number.isNaN(n) ? null : n);
                      }}
                      className="w-12 h-8 px-1 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs"
                    />
                  </div>
                  <p className="text-[11px] text-theme-text opacity-80 mt-1 overflow-hidden text-ellipsis" title={`${scriptPromotionValue ?? idea.context?.promotion_score ?? DEFAULT_PROMOTION}/10 - ${PROMOTION_COPY[scriptPromotionValue ?? idea.context?.promotion_score ?? DEFAULT_PROMOTION] ?? ""}`}>
                    {scriptPromotionValue ?? idea.context?.promotion_score ?? DEFAULT_PROMOTION}/10 - {PROMOTION_COPY[scriptPromotionValue ?? idea.context?.promotion_score ?? DEFAULT_PROMOTION] ?? ""}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-theme-text mb-2">
                    Optional script direction
                  </p>
                  <textarea
                    rows={3}
                    value={scriptPromptValue || ""}
                    onChange={(e) => onScriptPromptChange(idea.hash, e.target.value)}
                    placeholder={`e.g. more casual tone for "${idea.title || "this idea"}"`}
                    className="w-full px-3 py-2 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs placeholder-theme-text placeholder-opacity-50 resize-none"
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
        {hasScripts && baseUrlSlash
          ? scriptsByDate.map((s) => (
              <Link
                key={s.hash}
                to={`${baseUrlSlash}script/${s.hash}/`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-theme-secondary text-theme-text text-sm hover:bg-theme-main"
              >
                <HeroIcon name="clock" className="w-4 h-4" />
                View script ({formatEasyTime(s.create_date)})
              </Link>
            ))
          : null}
        <button
          type="button"
          onClick={() => onGenerateScript(idea, selectedBrandHash)}
          disabled={isGeneratingScript}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-theme-primary text-theme-button text-sm font-semibold hover:opacity-85 disabled:opacity-50"
        >
          <HeroIcon name="document-text" className="w-4 h-4" />
          {isGeneratingScript ? "Generating script..." : hasScripts ? "New script" : "Generate Script"}
        </button>
        </div>
      </div>
    </div>
  );
};

export default IdeaCard;
